// utils/localStorageUtils.js

import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "./constants";

// Function to set user data in localStorage
export const setUserData = (data) => {
  if (data) {
    localStorage.setItem("userData", JSON.stringify(data));
  } else {
    localStorage.removeItem("userData");
  }
};

// Function to get user data from localStorage
export const getUserData = () => {
  const data = localStorage.getItem("userData");
  return data ? JSON.parse(data) : null;
};

// Function to remove user data from localStorage
export const removeUserData = () => {
  localStorage.removeItem("userData");
};




// Helper function to get the full image URL
export const getImageUrl = (fileUrl) => {
  return fileUrl ? `${API_BASE_URL}${fileUrl}` : '';
};


// Utility function to format numbers
export const formatNumberUtil = (number) => {
  if (number >= 10000000) {
    const croreValue = (number / 10000000).toFixed(1);
    return croreValue.endsWith('.0') ? (number / 10000000).toFixed(0) + ' Cr' : croreValue + ' Cr';
  } else if (number >= 100000) {
    const lakhValue = (number / 100000).toFixed(1);
    return lakhValue.endsWith('.0') ? (number / 100000).toFixed(0) + ' Lac' : lakhValue + ' Lac';
  } else if (number >= 1000) {
    const thousandValue = (number / 1000).toFixed(1);
    return thousandValue.endsWith('.0') ? (number / 1000).toFixed(0) + 'k' : thousandValue + 'k';
  }
  return number; // For values less than 1000
};




export const formatLocationUtil = (location) => {
  if (!location) return "Location not available";

  const { searchLocation, city, locality, subLocality, apartmentSociety, houseDetails } = location;

  // Create an array of non-empty values
  const locationParts = [houseDetails, apartmentSociety, subLocality, locality, city, searchLocation]
    .filter(part => part) // Remove undefined/null/empty values
    .map(part => part.trim()); // Trim extra spaces

  return locationParts.length > 0 ? locationParts.join(", ") : "Location not available";
};


export const formatCurrencyUtil = (value) => {
  // Check for null, undefined, or empty values
  if (value === null || value === undefined || value === "" || isNaN(value)) {
    return "₹0"; // Default case
  }

  // Convert value to number if it's a string
  let num = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(num) || num < 0) return "₹0"; // Handle invalid cases

  if (num < 100000) {
    // Format below 1 lakh with commas
    return `₹${num.toLocaleString("en-IN")}`;
  } else if (num < 10000000) {
    // Convert to lakhs
    return `₹${(num / 100000).toFixed(1)} Lakh`;
  } else {
    // Convert to crores
    return `₹${(num / 10000000).toFixed(1)} Crore`;
  }
};



export function getMinimumFloorPriceMin(floorPricingDetails) {
  if (!Array.isArray(floorPricingDetails) || floorPricingDetails.length === 0) {
    return 0;
  }

  const prices = floorPricingDetails
    .map(floor => Number(floor.floorPriceMin))
    .filter(price => !isNaN(price) && price > 0);

  return prices.length > 0 ? Math.min(...prices) : 0;
}


export function getBhkTypesString(floorPricingDetails) {
  if (!Array.isArray(floorPricingDetails) || floorPricingDetails.length === 0) {
    return "";
  }

  const bhkOrder = {
    "Studio": 0,
    "1 BHK": 1,
    "2 BHK": 2,
    "3 BHK": 3,
    "4 BHK": 4,
    "5 BHK+": 5
  };

  const bhkTypes = floorPricingDetails
    .map(floor => floor.bhkType)
    .filter(bhk => bhk && bhkOrder.hasOwnProperty(bhk)) // Remove undefined/null and invalid values
    .sort((a, b) => bhkOrder[a] - bhkOrder[b]); // Sort based on predefined order

  if (bhkTypes.length === 0) {
    return ""; // Return empty string if no valid bhkType exists
  }

  const bhkNumbers = bhkTypes
    .map(bhk => bhk.replace(" BHK", "").replace("Studio", "Studio").replace("5+", "5+")); // Extract numbers

  return bhkNumbers.join(", ") + " BHK";
}


export const getMinMaxPrice = (floorPricingDetails) => {
  if (!Array.isArray(floorPricingDetails) || floorPricingDetails.length === 0) {
    return { minPrice: "", maxPrice: "" };
  }

  let minPrice = Infinity;
  let maxPrice = "";

  floorPricingDetails.forEach((floor) => {
    const min = floor.floorPriceMin ? Number(floor.floorPriceMin) : Infinity;
    const max = floor.floorPriceMax ? Number(floor.floorPriceMax) : "";

    if (min < minPrice) minPrice = min;
    if (max !== "" && (maxPrice === "" || max > maxPrice)) maxPrice = max;
  });

  return {
    minPrice: minPrice === Infinity ? "" : minPrice.toString(),
    maxPrice: maxPrice.toString(),
  };
};





// ✅ Pass `navigate` from the component to prevent reloading
export const withAuthCheck = (callback) => {
  return async (navigate, ...args) => {
    const userData = getUserData();

    if (!userData?._id) {
      navigate("/login", { replace: true });
      return;
    }

    return callback(userData, ...args); // Pass userData to the function
  };
};